/** Поездки.
 *
 * Поездка — период, а не счёт: траты внутри видны в статистике, но лимит не
 * трогают. Месяц с перелётом не должен выглядеть как месяц, в котором человек
 * сорвался (§16.25).
 *
 * Сколько потрачено в поездке, экран спрашивает у сводки по её датам — отдельного
 * счёта у поездки нет и заводить его было бы враньём.
 */
import { useEffect, useState } from "react";
import { ApiError, api, type Failure, type Summary, type Trip, type Trips } from "../lib/api.ts";
import { formatMoney } from "../lib/money.ts";
import { plural } from "../lib/plural.ts";
import { rowButton } from "../lib/rowStyle.ts";
import { tap } from "../lib/telegram.ts";
import { Header } from "../components/Header.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { Switch } from "../components/Switch.tsx";
import { Fallback } from "./Fallback.tsx";
import { card, hint } from "../components/ui.tsx";

export function TripsScreen({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<Trips | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [busy, setBusy] = useState(false);
  const [toggleTrouble, setToggleTrouble] = useState("");
  const [opened, setOpened] = useState<Trip | null>(null);

  const load = () => {
    setFailure(null);
    api.trips().then(setData)
      .catch((e) => setFailure(e instanceof ApiError ? e.kind : "broken"));
  };

  useEffect(load, []);

  if (failure) return <Fallback kind={failure} onRetry={load} />;

  const active = data?.items.find((t) => t.active) ?? null;

  return (
    <div style={{ padding: "var(--screen)" }}>
      <Header title="Поездки" onBack={onBack} />

      {!data ? <Skeleton /> : (
        <>
          <section style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: "var(--s-3)" }}>
              <div style={{ flexGrow: 1 }}>
                <div style={{ fontSize: "var(--f-body)", fontWeight: 600 }}>
                  {active ? "Я в поездке" : "Я дома"}
                </div>
                <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                              marginTop: 2 }}>
                  {active ? `${active.name} · с ${formatDay(active.started_on)}`
                          : "Включи, когда уедешь"}
                </div>
              </div>
              <Switch on={!!active} busy={busy}
                      label={active ? "Я в поездке, выключить" : "Я дома, включить поездку"}
                      onToggle={() => {
                        setBusy(true);
                        setToggleTrouble("");
                        api.toggleTrip().then(load).catch(() => setToggleTrouble("Не получилось переключить. Попробуй ещё раз."))
                          .finally(() => setBusy(false));
                      }} />
            </div>
            {toggleTrouble && (
              <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{toggleTrouble}</p>
            )}
            <p style={hint}>
              Траты в поездке пишутся и видны в статистике, но лимит не трогают: месяц с
              перелётом не должен выглядеть как месяц, в котором ты перебрал.
            </p>
          </section>

          {data.items.length === 0 ? (
            <p style={{ ...hint, textAlign: "center", marginTop: "var(--s-6)" }}>
              Поездок пока не было. Включи переключатель, когда уедешь, — траты этих дней не тронут лимит.
            </p>
          ) : (
            <section style={{ ...card, marginTop: "var(--s-3)", padding: "2px var(--s-5)" }}>
              {data.items.map((trip, i) => (
                <button key={trip.id} type="button" onClick={() => { tap(); setOpened(trip); }}
                        style={rowButton(i === data.items.length - 1)}>
                  <span style={{ flexGrow: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: "var(--f-body)", fontWeight: 500 }}>
                      {trip.name}
                      {trip.active && (
                        <span style={{ fontSize: "var(--f-micro)", color: "var(--income)",
                                       background: "color-mix(in srgb, var(--income) 12%, transparent)",
                                       borderRadius: 5, padding: "2px 6px", marginLeft: 7 }}>
                          идёт
                        </span>
                      )}
                    </span>
                    <span style={{ display: "block", fontSize: "var(--f-caption)",
                                   color: "var(--text-muted)", marginTop: 2 }}>
                      {span(trip)}
                    </span>
                  </span>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                       stroke="var(--text-disabled)" strokeWidth="2" strokeLinecap="round"
                       strokeLinejoin="round" aria-hidden="true">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
              ))}
            </section>
          )}
        </>
      )}

      {opened && (
        <TripSheet trip={opened} onClose={() => setOpened(null)}
                   onDone={() => { setOpened(null); load(); }} />
      )}
    </div>
  );
}

function TripSheet({ trip, onClose, onDone }: {
  trip: Trip; onClose: () => void; onDone: () => void;
}) {
  const [name, setName] = useState(trip.name);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");

  useEffect(() => {
    // Траты берём у сводки по датам поездки: своего счёта у неё нет.
    const to = trip.ended_on ?? new Date().toISOString().slice(0, 10);
    api.summary("month", { from: trip.started_on, to }).then(setSummary)
      .catch(() => undefined);
  }, [trip]);

  return (
    <Sheet title={trip.name} onClose={onClose}>
      {summary && (
        <div style={{ textAlign: "center", marginBottom: "var(--s-5)" }}>
          <div className="num" style={{ fontSize: "var(--f-t1)", fontWeight: 700,
                                        letterSpacing: "-0.8px" }}>
            {formatMoney(summary.expense_total, summary.base_currency)}
          </div>
          <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)", marginTop: 4 }}>
            за {plural(days(trip), ["день", "дня", "дней"])} · {span(trip)}
          </div>
        </div>
      )}

      <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                    marginBottom: "var(--s-2)" }}>
        Название
      </div>
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
             aria-label="Название поездки"
             style={{ width: "100%", minHeight: "var(--tap)", background: "var(--surface-2)",
                      border: "none", borderRadius: "var(--r-field)", padding: "12px 14px",
                      color: "var(--text)", font: "inherit", fontSize: "var(--f-body)",
                       }} />
      {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}
      <button type="button" disabled={busy || !name.trim() || name.trim() === trip.name}
              onClick={() => {
                tap();
                setBusy(true);
                setTrouble("");
                api.renameTrip(trip.id, name.trim()).then(onDone)
                  .catch(() => setTrouble("Не получилось сохранить. Попробуй ещё раз."))
                  .finally(() => setBusy(false));
              }}
              style={{ width: "100%", minHeight: "var(--tap)", marginTop: "var(--s-4)",
                       padding: 14, borderRadius: "var(--r-field)", border: "none",
                       cursor: "pointer", font: "inherit", fontSize: "var(--f-body)",
                       fontWeight: 600, background: "var(--accent)", color: "var(--on-accent)" }}>
        Сохранить
      </button>
    </Sheet>
  );
}

const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня",
                "июля", "августа", "сентября", "октября", "ноября", "декабря"];

function formatDay(iso: string): string {
  const [, month, day] = iso.split("-");
  return `${Number(day)} ${MONTHS[Number(month) - 1]}`;
}

function span(trip: Trip): string {
  return trip.ended_on
    ? `${formatDay(trip.started_on)} — ${formatDay(trip.ended_on)}`
    : `с ${formatDay(trip.started_on)}`;
}

function days(trip: Trip): number {
  const from = Date.parse(`${trip.started_on}T00:00:00`);
  const to = Date.parse(`${trip.ended_on ?? new Date().toISOString().slice(0, 10)}T00:00:00`);
  return Math.max(1, Math.round((to - from) / 86400000) + 1);
}

function Skeleton() {
  return (
    <div style={card} role="status" aria-busy="true" aria-label="Загружаю поездки">
      <div style={{ height: 11, width: "45%", background: "var(--surface-3)", borderRadius: 5 }} />
    </div>
  );
}

